import type { CityCode } from '@loci/city-editions';

import type { HrefSurface } from '@/lib/app-surface-href';
import { cityUrl } from '@/lib/cross-host-urls';

/**
 * Host surface that owns the current page. Mirrors `CitySurfaceLabel` without
 * importing the server-only `city-context` module (this helper is shared by
 * client Discover cards and server home-feed builders).
 *
 * Aliases the canonical {@link HrefSurface} so this builder and its mirror
 * image `buildAppSurfaceHref` (WEB-834) cannot drift apart.
 */
export type EventDetailHrefSurface = HrefSurface;

/**
 * Single source of truth for event-detail links (WEB-409, WEB-772).
 *
 * The canonical event-detail page lives on the **marketing-city** host
 * (`<city>.loci.city/event/<idOrSlug>` — `app/(city)/event/[slug]/page.tsx`).
 * There is no `/event/*` route on the app host.
 *
 * - `surface === 'app'` (default): jump to the city host via `cityUrl` so
 *   home-feed / app-surface cards do not 404 (WEB-409).
 * - any other surface (`marketing-city`, `fallback`, `marketing-apex`):
 *   same-origin `/event/<idOrSlug>` so staging catalog slugs resolve on
 *   the current environment instead of production (WEB-772).
 */
export function buildEventDetailHref(
  cityCode: CityCode,
  idOrSlug: string,
  surface: EventDetailHrefSurface = 'app',
): string {
  const path = `/event/${encodeURIComponent(idOrSlug)}`;
  return surface === 'app' ? cityUrl(cityCode, path) : path;
}
